import type * as zod from 'zod';

import {
  CustomerFeatureFlagDataZod,
  customerFeatureFlagDefinitions,
  customerFeatureFlags,
} from './customerFeatureFlags';
import type { CustomerFeatureFlagDefinitionSchema } from './customerFeatureFlags';
import type { DevelopmentFeatureFlagDefinitionSchema } from './developmentFeatureFlags';
import {
  DevelopmentFeatureFlagDataZod,
  developmentFeatureFlagDefinitions,
  underDevelopmentFeatureFlags,
} from './developmentFeatureFlags';
import type { InternalFeatureFlagDefinitionSchema } from './internalFeatureFlags';
import {
  InternalFeatureFlagDataZod,
  internalFeatureFlagDefinitions,
  internalTestingFeatureFlags,
} from './internalFeatureFlags';

// Consolidated zod schema for feathers-bot service
export const FeatureFlagDataZod = CustomerFeatureFlagDataZod.merge(
  InternalFeatureFlagDataZod,
).merge(DevelopmentFeatureFlagDataZod);

// Consolidated types
export type FeatureFlagData = zod.infer<typeof FeatureFlagDataZod>;
export type FeatureFlagKey = keyof FeatureFlagData;

// Section type shared across all feature flag categories
export type Section =
  | 'vision'
  | 'movement'
  | 'equipment'
  | 'infra'
  | 'safety'
  | 'newUI'
  | 'AI'
  | 'other'
  | 'environmentAndSpace';

// Base definition for all feature flags
export type BaseFeatureFlagDefinition = {
  title: string;
  description?: string;
  allowSingleUser?: boolean; // allow to be used by one user - only works for browser based flags
  mutuallyExclusiveWith?: string; // if set, this flag cannot be enabled at the same time as the specified flag (must be a valid FeatureFlagKey)
  requiresRestart?: boolean; // set false only when every consumer hot-reloads; absent ⇒ restart required
};

// Category type
export type Category = 'customer' | 'internal' | 'developing';

// Consolidated definition schema
export type FeatureFlagDefinitionSchema = (
  | CustomerFeatureFlagDefinitionSchema
  | DevelopmentFeatureFlagDefinitionSchema
  | InternalFeatureFlagDefinitionSchema
) & {
  category: Category;
  section?: Section;
};

// Consolidated feature flag definitions
export const featureFlagDefinitions: Record<
  string,
  FeatureFlagDefinitionSchema
> = {
  ...Object.fromEntries(
    Object.entries(customerFeatureFlagDefinitions).map(([key, def]) => [
      key,
      { ...def, category: 'customer' as const },
    ]),
  ),
  ...Object.fromEntries(
    Object.entries(internalFeatureFlagDefinitions).map(([key, def]) => [
      key,
      { ...def, category: 'internal' as const },
    ]),
  ),
  ...Object.fromEntries(
    Object.entries(developmentFeatureFlagDefinitions).map(([key, def]) => [
      key,
      { ...def, category: 'developing' as const },
    ]),
  ),
};

/**
 * Default feature flag values for Thor robots.
 *
 * Written to feature_flags.json when the file is missing (e.g. after factory
 * reset via "Reset user data"). Once written, they behave like any other
 * user-set flag. All flags that should be ON for Thor are listed explicitly
 * so the config is self-documenting and resilient to schema default changes.
 */
export const THOR_FLAG_DEFAULTS: Partial<Record<FeatureFlagKey, boolean>> = {
  // Customer
  legacyHaasSteps: true,
  etherNetIP: true,
  ros2: true,
  // Under Development — Movement
  batchJogDirectionsValidation: true,
  enableOptimizerLinearPlanner: true,
  disableWeldWeaving: true,
  disableArcStepInWeld: true,
  // Under Development — Safety
  treatPRCSyncAndInterpolatorErrorsAsWarnings: true,
  // Internal Testing — Movement
  doNotResetPositionOnTrajectoryReset: true,
  // Internal Testing — Safety
  enableJointStoppingAccelerations: true,
  disableErrorOnInvalidTrajectory: true,
};

// Restart required unless the flag's definition explicitly opts out; unknown keys default to required.
export function flagRequiresRestart(key: FeatureFlagKey): boolean {
  return featureFlagDefinitions[key]?.requiresRestart !== false;
}

// Re-export category-specific grouped flags
export {
  customerFeatureFlags,
  internalTestingFeatureFlags,
  underDevelopmentFeatureFlags,
};
